import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import MissingRequiredFieldsIcon from 'ext-ui/svg/icon/MissingRequiredFieldsIcon'
import RejectedTransactionsIcon from 'ext-ui/svg/icon/RejectedTransactionsIcon'
import { Location } from 'history'
import moment from 'moment'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectNotificationsUnreadCountFromState } from 'src/app/Notifications/redux/notifications.selectors'
import Box from 'src/components/Box/Box'
import HomeActionableTasksSection from 'src/components/HomeActivityBox/HomeActionableTasksSection'
import HomeActivityEmptyState from 'src/components/HomeActivityBox/HomeActivityEmptyState'
import HomeNotificationsSection from 'src/components/HomeActivityBox/HomeNotificationsSection/HomeNotificationsSection'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import SvgBudgetNeedsApproval from 'src/components/Svgs/SvgBudgetNeedsApproval'
import SvgExpensesToReview from 'src/components/Svgs/SvgExpensesToReview'
import SvgMissingExpenseCategoriesTask from 'src/components/Svgs/SvgMissingExpenseCategoriesTask'
import SvgMissingReceiptsTask from 'src/components/Svgs/SvgMissingReceiptsTask'
import SvgReimbursementsNeedReview from 'src/components/Svgs/SvgReimbursementsNeedReview'
import SvgVirtualCardNeedsApproval from 'src/components/Svgs/SvgVirtualCardNeedsApproval'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  activityUrl,
  budgetsNeedsApprovalUrl,
  managerReviewReimbursementsURL,
  managerReviewTransactionsURL,
  myWalletTransactionsURL,
  reimbursementsUrl,
  virtualCardsNeedsApprovalUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  useQueryGetUserNeedsApprovalStats,
  useQueryGetUserTaskStats,
} from 'src/queries/organizations/activity.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { LocationState } from 'src/redux/createRootReducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css, CSSProp } from 'styled-components'

export interface HomeActivityStat {
  icons: {
    staticIcon: React.ReactElement
    hoverIcon: React.ReactElement
  }
  text: React.ReactElement
  testId: string
  ariaLabelSource: string
  statNumber: number | undefined
  to?: Location<LocationState> | string
  onClick: () => void
  customCss?: CSSProp
  tooltipProps?: {
    tooltip: React.ReactElement
    tooltipFunction: (val: boolean) => void
    tooltipAnalytics: () => void
  }
}

// assumes stats are already loaded in
interface HomeActivityBoxProps {
  preReqStatsLoaded: boolean
  activeBudgets: number
  activeAccounts: number
}

const HomeActivityBox: React.FC<HomeActivityBoxProps> = (props) => {
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const user = useSelector(selectUserListModelFromState)
  const { ORGANIZATION_USER_STATISTICS: organizationUserStatistics } =
    useQueryUserFeatures([UserFeature.ORGANIZATION_USER_STATISTICS]).data || {}
  const hasFullOrgBookkeeperPermissions =
    organizationUserStatistics || user?.hasOrgAdminPermissions
  const notificationsUnreadCount = useSelector(
    selectNotificationsUnreadCountFromState,
  )

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const isManager = directReports !== undefined && directReports.length > 0
  const { data: taskStats, isPending: tasksLoading } =
    useQueryGetUserTaskStats()
  const { data: needsApprovalStats, isPending: needsApprovalLoading } =
    useQueryGetUserNeedsApprovalStats()

  const [
    rejectedTransactionsTooltipHover,
    setRejectedTransactionsTooltipHover,
  ] = useState<boolean>(false)
  const [missingReceiptsTooltipHover, setMissingReceiptsTooltipHover] =
    useState<boolean>(false)
  const [
    missingExpenseCategoriesTooltipHover,
    setMissingExpenseCategoriesTooltipHover,
  ] = useState<boolean>(false)
  const [
    missingRequiredFieldsTooltipHover,
    setMissingRequiredFieldsTooltipHover,
  ] = useState<boolean>(false)

  const budgetRequests = needsApprovalStats?.budgetsToApproveCount || 0
  const outOfPocketExpenses =
    needsApprovalStats?.reimbursementsToReviewCount || 0
  const managerApprovalReimbursements =
    needsApprovalStats?.managerReimbursementsToReviewCount || 0
  const expensesToReview = needsApprovalStats?.expensesToReviewCount || 0
  const virtualCardRequests =
    needsApprovalStats?.virtualCardsToApproveCount || 0

  const { show_expense_requirements: showAdditionalFields } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const rejectedTransactions = taskStats?.flaggedTransactionsCount || 0
  const missingReceipts = taskStats?.missingReceiptsCount || 0
  const missingExpenseCategories = taskStats?.missingExpenseCategoriesCount || 0
  const missingRequiredFields = taskStats?.missingRequiredFieldsCount || 0

  const sinceTimestamp = organization?.createdAt
    ? moment(organization.createdAt).subtract(2, 'months').format('YYYY-MM-DD')
    : '2017-01-01'

  const last90Days = moment().subtract(90, 'd').format('YYYY-MM-DD')
  const { analytics } = useAnalyticsContext()

  const oopeTurnedOn = !!organization?.isOutOfPocketExpensesVisible
  const userIsExpenseApprover = organization?.expenseApprover?.id === user?.id

  const hasRcnsOrBudgets = props.activeAccounts + props.activeBudgets > 0
  const hasApprovals =
    virtualCardRequests + budgetRequests > 0 && hasRcnsOrBudgets
  const hasItemsToReview = outOfPocketExpenses > 0 || expensesToReview > 0
  const hasTasks =
    missingReceipts +
      missingExpenseCategories +
      (showAdditionalFields ? missingRequiredFields : 0) +
      (managerReviewRulesEnabled ? rejectedTransactions : 0) >
    0
  const hasNotifications = notificationsUnreadCount > 0
  const hasNothingToDo =
    !hasApprovals && !hasTasks && !hasNotifications && !hasItemsToReview

  return (
    <Box padding={`20px`}>
      <div
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 20px;
        `}
      >
        <Trns source={'Activity'} />
      </div>
      {!hasNothingToDo &&
        (hasFullOrgBookkeeperPermissions || hasRcnsOrBudgets || isManager) && (
          <HomeActionableTasksSection // assumes stats are already loaded in
            title={'Needs Approval'}
            testId={'needsApproval'}
            loadingProps={{
              loadingState: needsApprovalLoading || !props.preReqStatsLoaded,
            }}
            emptyStateSublabel={
              "You're up to date! We'll notify you when new items need review."
            }
            stats={[
              ...(!hasApprovals
                ? []
                : [
                    {
                      icons: {
                        staticIcon: <SvgVirtualCardNeedsApproval />,
                        hoverIcon: <SvgVirtualCardNeedsApproval />,
                      },
                      to: virtualCardsNeedsApprovalUrl(),
                      ariaLabelSource: 'Virtual cards to approve',
                      text: <Trns source={'Virtual cards to approve'} />,
                      testId: 'virtualCardsToApprove',
                      statNumber: virtualCardRequests,
                      onClick: (): void => {
                        analytics?.track('needs your approval - card requests')
                      },
                    },
                    ...(props.activeAccounts > 0
                      ? [
                          {
                            icons: {
                              staticIcon: <SvgBudgetNeedsApproval />,
                              hoverIcon: <SvgBudgetNeedsApproval />,
                            },
                            to: budgetsNeedsApprovalUrl(),
                            ariaLabelSource: 'Budgets to approve',
                            text: <Trns source={'Budgets to approve'} />,
                            testId: 'budgetsToApprove',
                            statNumber: budgetRequests,
                            onClick: (): void => {
                              analytics?.track(
                                'needs your approval - budget requests',
                              )
                            },
                          },
                        ]
                      : []),
                  ]),
              {
                icons: {
                  staticIcon: <SvgExpensesToReview />,
                  hoverIcon: <SvgExpensesToReview />,
                },
                to: managerReviewTransactionsURL(),
                ariaLabelSource: 'Expenses to review',
                text: <Trns source={'Expenses to review'} />,
                testId: 'expensesToReview',
                statNumber: expensesToReview,
                onClick: (): void => {
                  analytics?.track('needs your approval - expenses')
                },
              },
              ...(userIsExpenseApprover && oopeTurnedOn
                ? [
                    {
                      icons: {
                        staticIcon: <SvgReimbursementsNeedReview />,
                        hoverIcon: <SvgReimbursementsNeedReview />,
                      },
                      to: {
                        pathname: reimbursementsUrl(),
                        search: `?statuses=PENDING&sinceTimestamp=${sinceTimestamp}`,
                        state: {},
                        hash: '',
                      },
                      ariaLabelSource: 'Reimbursements to be paid',
                      text: <Trns source={'Reimbursements to be paid'} />,
                      testId: 'reimbursementsToReview',
                      statNumber: outOfPocketExpenses,
                      onClick: (): void => {
                        analytics?.track('needs your approval - oope reports')
                      },
                    },
                  ]
                : []),
              ...(isManager && oopeTurnedOn
                ? [
                    {
                      icons: {
                        staticIcon: <SvgReimbursementsNeedReview />,
                        hoverIcon: <SvgReimbursementsNeedReview />,
                      },
                      to: managerReviewReimbursementsURL(),
                      ariaLabelSource: 'Reimbursements to review',
                      text: <Trns source={'Reimbursements to review'} />,
                      testId: 'managerReimbursementsToReview',
                      statNumber: managerApprovalReimbursements,
                      onClick: (): void => {
                        analytics?.track(
                          'needs your manager approval - oope reports',
                        )
                      },
                    },
                  ]
                : []),
            ]}
          />
        )}
      {!hasNothingToDo && (
        <>
          <HomeActionableTasksSection
            title={'My Tasks'}
            testId={'myTasks'}
            loadingProps={{
              loadingState: tasksLoading,
            }}
            showLoadingState={true}
            emptyStateSublabel={'No tasks on your plate. Enjoy the moment!'}
            stats={[
              ...(managerReviewRulesEnabled
                ? [
                    {
                      icons: {
                        staticIcon: <RejectedTransactionsIcon />,
                        hoverIcon: <RejectedTransactionsIcon />,
                      },
                      text: <Trns source={'Rejected Transactions'} />,
                      testId: 'rejectedTransactions',
                      ariaLabelSource: 'Rejected Transactions',
                      statNumber:
                        rejectedTransactions !== undefined
                          ? rejectedTransactions
                          : undefined,
                      to: activityUrl(),
                      onClick: (): void => {
                        analytics?.track(
                          'action required - rejected transactions',
                        )
                      },
                      tooltipProps: {
                        tooltip: (
                          <HoverTooltip
                            css={css`
                              white-space: normal;
                            `}
                            show={rejectedTransactionsTooltipHover}
                            tooltipText={
                              <Trns
                                source={
                                  'Transactions your manager rejected because more info or corrections are needed'
                                }
                              />
                            }
                          />
                        ),
                        tooltipFunction: setRejectedTransactionsTooltipHover,
                        tooltipAnalytics: (): void => {
                          analytics?.track('home page - alert tooltips', {
                            rejected_transactions: true,
                          })
                        },
                      },
                    },
                  ]
                : []),
              {
                icons: {
                  staticIcon: <SvgMissingReceiptsTask />,
                  hoverIcon: <SvgMissingReceiptsTask />,
                },
                text: <Trns source={'Missing Receipts'} />,
                testId: 'missingReceipts',
                ariaLabelSource: 'Missing Receipts',
                statNumber:
                  missingReceipts !== undefined ? missingReceipts : undefined,
                to: {
                  pathname: myWalletTransactionsURL(),
                  search: `?receiptRequired=true&receiptStatus=Missing&since=${last90Days}&recipientOrPhysicalCardUserId=${user?.id}`,
                  state: {},
                  hash: '',
                  key: '',
                },
                onClick: (): void => {
                  analytics?.track('action required - missing receipts stat')
                },
                tooltipProps: {
                  tooltip: (
                    <HoverTooltip
                      css={css`
                        white-space: normal;
                      `}
                      show={missingReceiptsTooltipHover}
                      tooltipText={
                        <Trns
                          source={
                            'Transactions cleared in the last 90 days missing receipts'
                          }
                        />
                      }
                    />
                  ),
                  tooltipFunction: setMissingReceiptsTooltipHover,
                  tooltipAnalytics: (): void => {
                    analytics?.track('home page - alert tooltips', {
                      missing_receipt: true,
                    })
                  },
                },
              },
              {
                icons: {
                  staticIcon: <SvgMissingExpenseCategoriesTask />,
                  hoverIcon: <SvgMissingExpenseCategoriesTask />,
                },
                text: <Trns source={'Missing Expense Categories'} />,
                testId: 'missingExpenseCategories',
                ariaLabelSource: 'Missing Expense Categories',
                statNumber:
                  missingExpenseCategories !== undefined
                    ? missingExpenseCategories
                    : undefined,
                to: {
                  pathname: myWalletTransactionsURL(),
                  search: `?expenseCategoryStatuses=Missing&since=${last90Days}&recipientOrPhysicalCardUserId=${user?.id}`,
                  state: {},
                  hash: '',
                  key: '',
                },
                onClick: (): void => {
                  analytics?.track(
                    'action required - missing expense categories',
                  )
                },
                tooltipProps: {
                  tooltip: (
                    <HoverTooltip
                      css={css`
                        white-space: normal;
                      `}
                      show={missingExpenseCategoriesTooltipHover}
                      tooltipText={
                        <Trns
                          source={
                            'Transactions cleared in the last 90 days missing expense categories'
                          }
                        />
                      }
                    />
                  ),
                  tooltipFunction: setMissingExpenseCategoriesTooltipHover,
                  tooltipAnalytics: (): void => {
                    analytics?.track('home page - alert tooltips', {
                      missing_expense_category: true,
                    })
                  },
                },
              },
              ...(showAdditionalFields
                ? [
                    {
                      icons: {
                        staticIcon: <MissingRequiredFieldsIcon />,
                        hoverIcon: <MissingRequiredFieldsIcon />,
                      },
                      text: <Trns source={'Missing Required Fields'} />,
                      testId: 'missingRequiredFields',
                      ariaLabelSource: 'Missing Required Fields',
                      statNumber:
                        missingRequiredFields !== undefined
                          ? missingRequiredFields
                          : undefined,
                      to: activityUrl(),
                      onClick: (): void => {
                        analytics?.track(
                          'action required - missing required fields',
                        )
                      },
                      tooltipProps: {
                        tooltip: (
                          <HoverTooltip
                            css={css`
                              white-space: normal;
                            `}
                            show={missingRequiredFieldsTooltipHover}
                            tooltipText={
                              <Trns
                                source={
                                  'Transactions cleared in the last 90 days missing required fields'
                                }
                              />
                            }
                          />
                        ),
                        tooltipFunction: setMissingRequiredFieldsTooltipHover,
                        tooltipAnalytics: (): void => {
                          analytics?.track('home page - alert tooltips', {
                            missing_required_fields: true,
                          })
                        },
                      },
                    },
                  ]
                : []),
            ]}
          />
          <HomeNotificationsSection />
        </>
      )}
      {hasNothingToDo && (
        <HomeActivityEmptyState
          sublabel={
            "You've completed all your tasks and have no unread notifications. Great job staying on top of everything!"
          }
          showIcon={true}
          aligment={'center'}
        />
      )}
    </Box>
  )
}

export default HomeActivityBox
